import { type ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { StediPayerSearch } from '@features/payer-search/components/StediPayerSearch';
import { useGetAdminPayersQuery } from '@features/payer-search/store/api';
import type { Payer } from '@features/payer-search/types';
import { useDebounce } from '@hooks/useDebounce';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';

import {
  useCreateCredentialingStatusMutation,
  useGetAdminCredentialingStatusesQuery,
  useUpdateCredentialingStatusMutation,
} from '../../store/api';
import type { CredentialingStatus, PublicCredentialingStatus } from '../../types';

import { AdminCredentialingPayerName } from './CredentialingPayerName';
import { CREDENTIALING_PAGE_SIZE } from './credentialingStatus';
import { CredentialingStatusPanel, CredentialingStatusSelect } from './CredentialingStatusPanel';

export interface CredentialingProviderOption {
  id: string;
  label: string;
}

/** The owning route supplies its evaluated V3 gate; this component also checks staff permissions. */
export function AdminCredentialingStatuses({
  organizationId,
  userId,
  enabled,
  providers,
  providerNotice,
  onDirtyChange,
}: {
  organizationId: string;
  userId?: string;
  enabled: boolean;
  providers: CredentialingProviderOption[];
  providerNotice?: ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const roles: unknown = user?.['https://api.smartscribe.health/roles'];
  const admin = Array.isArray(roles) && roles.includes('Admin');
  const canRead = admin || (Array.isArray(roles) && roles.includes('Moderator'));
  if (!enabled || !organizationId || isLoading || !isAuthenticated || !canRead) return null;
  return (
    <ScopedAdminCredentialingStatuses
      key={JSON.stringify([organizationId, userId, user?.sub, admin])}
      organizationId={organizationId}
      userId={userId}
      providers={providers}
      providerNotice={providerNotice}
      onDirtyChange={onDirtyChange}
      canWrite={admin}
    />
  );
}

function ScopedAdminCredentialingStatuses({
  organizationId,
  userId,
  providers,
  providerNotice,
  onDirtyChange,
  canWrite,
}: {
  organizationId: string;
  userId?: string;
  providers: CredentialingProviderOption[];
  providerNotice?: ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
  canWrite: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [provider, setProvider] = useState(userId ?? '');
  const [payer, setPayer] = useState<Payer | null>(null);
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), 300);
  const directory = useGetAdminPayersQuery(
    { search: term, perPage: 50 },
    { skip: !canWrite || term.length < 2 },
  );
  const [status, setStatus] = useState<CredentialingStatus>('not_started');
  const [message, setMessage] = useState<{ error: boolean; text: string }>();
  const query = useGetAdminCredentialingStatusesQuery(
    { organizationId, userId, limit: CREDENTIALING_PAGE_SIZE, offset },
    { refetchOnMountOrArgChange: true },
  );
  const [create, creating] = useCreateCredentialingStatusMutation();
  const [update, updating] = useUpdateCredentialingStatusMutation();
  const saving = creating.isLoading || updating.isLoading;
  const draftKey = JSON.stringify([provider, payer?.id ?? '', search, status]);
  const [savedDraftKey, setSavedDraftKey] = useState(
    JSON.stringify([userId ?? '', '', '', 'not_started']),
  );
  useEffect(() => {
    onDirtyChange?.(saving || draftKey !== savedDraftKey);
  }, [draftKey, savedDraftKey, saving, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const rows = query.currentData?.items ?? [];
  const wrongScope = rows.some(
    row => row.organization_id !== organizationId || (userId && row.user_id !== userId),
  );
  const failed = query.isError || wrongScope;
  const save = async (row?: PublicCredentialingStatus, nextStatus = status) => {
    if (!canWrite || saving || (row && row.organization_id !== organizationId)) return;
    const payerId = payer?.id;
    if (!row && (!payerId || !providers.some(item => item.id === provider))) return;
    setMessage(undefined);
    try {
      const result = row
        ? await update({
            organizationId,
            credentialingStatusId: row.id,
            updateCredentialingStatusRequest: { status: nextStatus },
          }).unwrap()
        : await create({
            organizationId,
            createCredentialingStatusRequest: {
              user_id: provider,
              payer_uuid: payerId ?? '',
              status: nextStatus,
            },
          }).unwrap();
      if (result.organization_id !== organizationId || (userId && result.user_id !== userId))
        throw new Error('Scope mismatch');
      if (!row) setSavedDraftKey(draftKey);
      setMessage({ error: false, text: 'Credentialing status saved.' });
    } catch {
      setMessage({
        error: true,
        text: 'We couldn’t save this status. Refresh to check for an existing record before retrying.',
      });
    }
  };
  return (
    <CredentialingStatusPanel
      rows={failed ? [] : rows}
      pending={query.isFetching}
      failed={failed}
      offset={offset}
      onPage={setOffset}
      onRetry={() => void query.refetch()}
      providerLabel={id => providers.find(item => item.id === id)?.label ?? id}
      payerLabel={id =>
        payer?.id === id ? payer.display_name : <AdminCredentialingPayerName id={id} />
      }
      onUpdate={canWrite ? (row, value) => void save(row, value) : undefined}
      saving={saving}
    >
      {providerNotice}
      {canWrite && (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Provider"
              value={provider}
              onChange={event => setProvider(event.target.value)}
              disabled={saving || Boolean(userId)}
              sx={{ minWidth: 220 }}
            >
              {providers.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack sx={{ minWidth: 280, flex: 1 }}>
              <StediPayerSearch
                label="Payer"
                disabled={saving}
                inputValue={search}
                onInputChange={value => {
                  setSearch(value);
                  setPayer(null);
                }}
                loading={directory.isFetching}
                error={directory.isError}
                options={(directory.currentData?.items ?? [])
                  .filter(item => item.id)
                  .map(item => ({
                    displayName: item.display_name,
                    stediId: item.stedi_id,
                    payerId: item.primary_payer_id,
                  }))}
                value={payer ? { displayName: payer.display_name, stediId: payer.stedi_id } : null}
                onSelect={option => {
                  const selected =
                    directory.currentData?.items.find(item => item.stedi_id === option?.stediId) ??
                    null;
                  setPayer(selected);
                  setSearch(selected?.display_name ?? '');
                }}
              />
            </Stack>
            <CredentialingStatusSelect value={status} onChange={setStatus} disabled={saving} />
          </Stack>
          <Button
            variant="contained"
            disabled={saving || !provider || !payer?.id}
            onClick={() => void save()}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add credentialing status
          </Button>
        </Stack>
      )}
      {message && <Alert severity={message.error ? 'error' : 'success'}>{message.text}</Alert>}
    </CredentialingStatusPanel>
  );
}
